// -----------------------------------------------------------------------------
// src/Components/UnifiedOfferWizard.js (UPDATED - With Budget Validation)
// -----------------------------------------------------------------------------
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
  Chip,
  Alert,
  Avatar,
  Stack,
  IconButton,
  Fade,
  useMediaQuery,
  Paper,
  Divider,
  Collapse,
  AlertTitle,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useMutation, useQuery } from 'react-query';

// Icons
import BusinessIcon from '@mui/icons-material/Business';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import SendIcon from '@mui/icons-material/Send';
import CreateIcon from '@mui/icons-material/Create';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  AccountBalanceWallet as WalletIcon,
  Warning as WarningIcon,
  AddCircle as AddCircleIcon,
} from '@mui/icons-material';


// Platform icons
import { 
  VideoLibrary as YouTubeIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  MusicNote as TikTokIcon,
} from '@mui/icons-material';

import useAuth from '../Hooks/use-auth';
import useAlertDialog from '../Components/useAlertDialog';
import AlertDialog from '../Components/AlertDialog';
import AddBalanceDialog from '../Components/tools/addBalance';
import API from '../API';
import defaultPfp from '../Components/globalAssets/ppfLogo.png';
import { 
  getPlatformAndPromotion, 
  parseLocalFloat, 
  platformRatesListFromCreator 
} from '../Utils/constants';

const deliverableTypeOptions = [
  {
    label: "Standard Offer",
    value: "offer",
    description: "A comprehensive collaboration with clear deliverables and payment terms",
    icon: "💼",
  },
  {
    label: "Performance Bonus",
    value: "bonus", 
    description: "Additional incentive or reward for exceptional performance",
    icon: "🏆",
  },
  {
    label: "Revision Request",
    value: "revision",
    description: "Request for changes or improvements to existing work",
    icon: "✏️",
  },
  {
    label: "Additional Work",
    value: "additional_work",
    description: "Extra work beyond the original scope",
    icon: "➕",
  }
];

const platformOptions = [
  { 
    value: "TikTok", 
    color: "error", 
    icon: TikTokIcon,
  },
  { 
    value: "Instagram", 
    color: "secondary", 
    icon: InstagramIcon,
  },
  { 
    value: "YouTube", 
    color: "error", 
    icon: YouTubeIcon,
  },
  { 
    value: "Twitter", 
    color: "info", 
    icon: TwitterIcon,
  },
  { 
    value: "LinkedIn", 
    color: "info", 
    icon: LinkedInIcon,
  }
];

const steps = [
  {
    label: "Details",
    mobileLabel: "Details",
    icon: BusinessIcon,
  },
  {
    label: "Review",
    mobileLabel: "Send",
    icon: CheckCircleIcon,
  }
];

// Helper function to get default due date (7 days from now)
const getDefaultDueDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  return date.toISOString().slice(0, 10);
};

/**
 * UnifiedOfferWizard - Simplified, clean offer creation wizard
 * 
 * NEW PROP:
 * - isInDialog: boolean - When true, adjusts padding for dialog usage
 */
export default function UnifiedOfferWizard({
  creator,
  isExternal = true,
  isCelebrity = false,
  partnershipId = null,
  conversationId = null,
  onComplete,
  onCancel,
  isInDialog = false,
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { token: currentToken, getCurrrentUser } = useAuth();
  const currentUser = getCurrrentUser();
  const { dialogState, openDialog, closeDialog } = useAlertDialog();
  const isBookingDialog = isExternal && isInDialog;
  const isChatOfferDialog = !isExternal && isInDialog;
  const useCleanDialogStyle = isBookingDialog || isChatOfferDialog;
  const wizardPalette = useMemo(
    () => ({
      canvas:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.default, 0.98)
          : '#eef4f8',
      surface:
        theme.palette.mode === 'dark'
          ? theme.palette.background.paper
          : '#ffffff',
      surfaceMuted:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.paper, 0.84)
          : '#f5f2ef',
      surfaceAccent:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.paper, 0.92)
          : '#fbf5f3',
      border:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.common.white, 0.08)
          : alpha('#0f172a', 0.1),
      accent: '#a00012',
      accentSoft: alpha('#a00012', 0.1),
      accentAlt: '#744540',
      accentWarm: '#8f3a35',
      ink: theme.palette.mode === 'dark' ? theme.palette.text.primary : '#1c1917',
      muted: theme.palette.mode === 'dark' ? theme.palette.text.secondary : '#7a6662',
      shadow:
        theme.palette.mode === 'dark'
          ? '0 18px 48px rgba(0,0,0,0.34)'
          : '0 24px 60px rgba(15, 23, 42, 0.08)',
    }),
    [theme]
  );

  const sectionCardSx = {
    border: `1px solid ${useCleanDialogStyle ? '#eee7e1' : alpha(wizardPalette.ink, 0.14)}`,
    borderRadius: useCleanDialogStyle ? 3 : 5,
    bgcolor: wizardPalette.surface,
    boxShadow: useCleanDialogStyle
      ? '0 12px 34px rgba(65, 42, 35, 0.052)'
      : "0 8px 24px rgba(15, 23, 42, 0.04)",
  };

  const fieldLabelSx = {
    mb: 0.75,
    fontSize: useCleanDialogStyle ? "0.68rem" : "0.76rem",
    fontWeight: 800,
    color: wizardPalette.muted,
    textTransform: 'uppercase',
    letterSpacing: useCleanDialogStyle ? '0.02em' : '0.08em',
  };

  const helperTextSx = {
    mt: 0.65,
    ml: 0.25,
    fontSize: useCleanDialogStyle ? "0.76rem" : "0.8rem",
    color: wizardPalette.muted,
    lineHeight: 1.45,
  };

  const fieldGroupSx = {
    p: { xs: useCleanDialogStyle ? 0 : 1.2, md: useCleanDialogStyle ? 0 : 1.45 },
    borderRadius: useCleanDialogStyle ? 0 : 3.5,
    border: useCleanDialogStyle ? 'none' : `1px solid ${alpha(wizardPalette.ink, 0.14)}`,
    bgcolor: useCleanDialogStyle ? 'transparent' : '#f2f6fb',
    boxShadow: useCleanDialogStyle
      ? 'none'
      : 'inset 0 1px 0 rgba(255,255,255,0.78), 0 1px 2px rgba(15, 23, 42, 0.03)',
  };

  const descriptionPanelSx = {
    ...fieldGroupSx,
    p: { xs: useCleanDialogStyle ? 0 : 1.2, md: useCleanDialogStyle ? 0 : 1.45 },
    borderRadius: useCleanDialogStyle ? 0 : 3.5,
    border: useCleanDialogStyle ? 'none' : `1px solid ${alpha(wizardPalette.accentAlt, 0.22)}`,
    bgcolor: useCleanDialogStyle ? 'transparent' : '#e9f2ee',
  };

  const wizardFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: useCleanDialogStyle ? 1.4 : 2.75,
      bgcolor: wizardPalette.surface,
      minHeight: useCleanDialogStyle ? 44 : 52,
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
      '& fieldset': {
        borderColor: useCleanDialogStyle
          ? '#8d969f'
          : alpha(wizardPalette.ink, theme.palette.mode === 'dark' ? 0.2 : 0.16),
      },
      '&:hover fieldset': {
        borderColor: useCleanDialogStyle ? '#6f7782' : alpha(wizardPalette.accent, 0.42),
      },
      '&.Mui-focused fieldset': {
        borderColor: wizardPalette.accent,
        borderWidth: 1.5,
      },
      '&.Mui-focused': {
        boxShadow: useCleanDialogStyle ? `0 0 0 3px ${alpha(wizardPalette.accent, 0.08)}` : 'none',
      },
    },
    '& .MuiInputBase-input': {
      fontWeight: 600,
      fontSize: useCleanDialogStyle ? '0.84rem' : '0.95rem',
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
      color: wizardPalette.accent,
    },
  };

  const renderHelperText = (message, error) => (
    useCleanDialogStyle && !error ? null : (
      <Typography
        variant="caption"
        sx={{
          ...helperTextSx,
          color: error ? theme.palette.error.main : wizardPalette.muted,
        }}
      >
        {error || message}
      </Typography>
    )
  );

  // Wizard state
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showAddBalanceDialog, setShowAddBalanceDialog] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    partnershipName: '',
    description: '',
    offerAmount: isCelebrity ? '' : '50',
    deliverableType: 'offer',
    platforms: [],
    dueDate: getDefaultDueDate(),
    revisions: '',
    scriptRequirements: '',
    email: '',
    sendNotifications: true,
  });

  const [errors, setErrors] = useState({});

  // ═══════════════════════════════════════════════════════════════════
  // FETCH BALANCE AND CREDIT INFO (for internal offers only)
  // ═══════════════════════════════════════════════════════════════════
  
  const shouldCheckBudget = !isExternal && currentToken && !!currentUser?.id;

  // Fetch user's current balance
  const { 
    data: balanceData, 
    isLoading: balanceLoading,
    refetch: refetchBalance
  } = useQuery(
    ["myBalance"],
    () => API.balanceLedger.fetchMyBalance(),
    {
      enabled: shouldCheckBudget,
      staleTime: 30000,
      refetchOnWindowFocus: false,
    }
  );

  // Fetch company info to check BlitzPay account and spending power
  const { 
    data: companyInfo,
    isLoading: companyLoading
  } = useQuery(
    ["company", currentUser?.company_id],
    () => API.companies.listFetch({ id: currentUser?.company_id }),
    { 
      enabled: shouldCheckBudget && !!currentUser?.company_id,
      staleTime: 60000,
      refetchOnWindowFocus: false,
    }
  );

  // Fetch spending info if BlitzPay account
  const hasBlitzPayAccount = companyInfo?.blitzpay_account === true;

  const {
    data: spendingInfo,
    isLoading: spendingLoading
  } = useQuery(
    ["mySpendingInfo"],
    () => API.teams.getMySpendingInfo(),
    {
      enabled: shouldCheckBudget && hasBlitzPayAccount,
      staleTime: 60000,
      refetchOnWindowFocus: false,
    }
  );

  // ═══════════════════════════════════════════════════════════════════
  // CALCULATE AFFORDABILITY
  // ═══════════════════════════════════════════════════════════════════
  
  const maxAffordableBudget = useMemo(() => {
    if (!shouldCheckBudget) return Infinity; // Skip check for external offers
    
    const currentBalance = parseFloat(balanceData?.balance || 0);
    
    if (hasBlitzPayAccount && spendingInfo) {
      const availableSpending = parseFloat(spendingInfo.total_available_funds || 0);
      return currentBalance + availableSpending;
    }

    return currentBalance;
  }, [balanceData, spendingInfo, hasBlitzPayAccount, shouldCheckBudget]);

  const offerAmount = parseFloat(formData.offerAmount || 0);
  const canAffordOffer = !shouldCheckBudget || offerAmount <= maxAffordableBudget;
  const isLoadingFunds = shouldCheckBudget && (balanceLoading || companyLoading || (hasBlitzPayAccount && spendingLoading));
  const shortfall = Math.max(0, offerAmount - maxAffordableBudget);

  // Get creator promotions for auto-fill
  const promotions = useMemo(() => platformRatesListFromCreator(creator), [creator]);

  // Initialize with sensible defaults
  useEffect(() => {
    if (!promotions.length) return;
    
    const defaultPromo = promotions[promotions[1] ? 1 : 0];
    if (defaultPromo && !isCelebrity) {
      const recommended = creator[defaultPromo.rateKey] ?? 50;
      setFormData(prev => ({
        ...prev,
        offerAmount: recommended.toString(),
        platforms: [getPlatformAndPromotion(defaultPromo.rateKey).platform]
      }));
    }

    // Auto-fill partnership name
    setFormData(prev => ({
      ...prev,
      partnershipName: prev.partnershipName || `${isExternal ? 'Partnership' : 'Offer'} with ${creator.display_name}`
    }));
  }, [promotions, creator, isCelebrity, isExternal]);

  // Form handlers
  const handleChange = (field) => (event) => {
    const { value, checked, type } = event.target;
    setFormData(prev => ({
      ...prev,
      [field]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handlePlatformToggle = (platformValue) => {
    setFormData(prev => {
      const currentPlatforms = Array.isArray(prev.platforms) ? prev.platforms : [];
      const isSelected = currentPlatforms.includes(platformValue);

      return {
        ...prev,
        platforms: isSelected
          ? currentPlatforms.filter(value => value !== platformValue)
          : [...currentPlatforms, platformValue]
      };
    });

    if (errors.platforms) {
      setErrors(prev => ({ ...prev, platforms: null }));
    }
  };

  const handleAddBalanceSuccess = () => {
    refetchBalance();
    setShowAddBalanceDialog(false);
  };

  // Validation
  const validateStep = (stepIndex) => {
    const newErrors = {};

    switch (stepIndex) {
      case 0:
        if (!formData.partnershipName.trim()) {
          newErrors.partnershipName = "Partnership name is required";
        }
        if (!formData.description.trim()) {
          newErrors.description = "Description is required";
        }
        if (!formData.offerAmount || parseFloat(formData.offerAmount) <= 0) {
          newErrors.offerAmount = "Valid offer amount is required";
        }
        if (shouldCheckBudget && !canAffordOffer) {
          newErrors.offerAmount = `Insufficient funds. You need $${shortfall.toFixed(2)} more.`;
        }
        break;
      
      case 1:
        if (isExternal && !currentToken && !formData.email.trim()) {
          newErrors.email = "Email is required";
        }
        if (shouldCheckBudget && !canAffordOffer) {
          newErrors.budget = "Insufficient funds to send this offer";
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigation
  const handleNext = () => {
    if (validateStep(activeStep)) {
      setCompletedSteps(prev => new Set([...prev, activeStep]));
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  // API mutation
  const createPartnershipMutation = useMutation(
    isExternal ? API.partnerships.book : API.deliverables.create,
    {
      onSuccess: (data) => {
        if (isExternal) {
          if (currentToken) {
            openDialog(
              'Partnership Created!',
              'Your partnership has been created successfully. You can view it in your dashboard.',
              () => {
                navigate(`/partnerships/${data.partnership.id}`);
                onComplete?.(data);
              },
              () => {
                navigate(`/partnerships/${data.partnership.id}`);
                onComplete?.(data);
              },
              'View Partnership'
            );
          } else {
            const params = new URLSearchParams();
            params.set('tab', 'brand');
            if (formData.email) params.set('email', formData.email);
            navigate(`/onboard?${params.toString()}`);
            onComplete?.(data);
          }
        } else {
          openDialog(
            'Offer Sent!',
            'Your offer has been sent successfully. The creator will be notified.',
            () => onComplete?.(data),
            () => onComplete?.(data),
            'Done'
          );
        }
      },
      onError: (error) => {
        openDialog(
          'Error',
          `Something went wrong: ${error?.response?.data?.error || error.message}`,
          closeDialog,
          closeDialog,
          'OK'
        );
      }
    }
  );

  const handleSubmit = () => {
    if (!validateStep(activeStep)) return;

    const today = new Date().toISOString().slice(0, 10);
    
    if (isExternal) {
      const payload = {
        creator: creator.creator || creator.display_name,
        name: formData.partnershipName,
        description: formData.description,
        proposal_date: today,
        notes: formData.description,
        ...(!currentToken && { email: formData.email }),
        
        deliverable: {
          deliverable_type: formData.deliverableType,
          amount: parseLocalFloat(formData.offerAmount),
          notes: formData.description,
          due_date: formData.dueDate || null,
          platforms: formData.platforms.length > 0 ? formData.platforms : null,
          revision: formData.revisions ? parseInt(formData.revisions) : null,
          script: formData.scriptRequirements || null,
        }
      };
      
      createPartnershipMutation.mutate(payload);
    } else {
      const payload = {
        partnership_id: partnershipId,
        creator_id: creator.creator || creator.display_name,
        conversation_id: conversationId,
        deliverable_type: formData.deliverableType,
        amount: parseLocalFloat(formData.offerAmount),
        status: "sent",
        notes: formData.description,
        due_date: formData.dueDate || null,
        platforms: formData.platforms.length > 0 ? formData.platforms : null,
        revision: formData.revisions ? parseInt(formData.revisions) : null,
        script: formData.scriptRequirements || null,
      };
      
      createPartnershipMutation.mutate(payload);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // RENDER AFFORDABILITY CHECK
  // ═══════════════════════════════════════════════════════════════════

  const renderAffordabilityCheck = () => {
    if (!shouldCheckBudget) return null; // Skip for external offers

    if (isLoadingFunds) {
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={16} />
            <Typography variant="body2">
              Checking your available funds...
            </Typography>
          </Stack>
        </Alert>
      );
    }

    const currentBalance = parseFloat(balanceData?.balance || 0);
    const spendingAvailable = hasBlitzPayAccount ? parseFloat(spendingInfo?.total_available_funds || 0) : 0;

    if (!canAffordOffer && offerAmount > 0) {
      return (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => setShowAddBalanceDialog(true)}
              startIcon={<AddCircleIcon />}
            >
              Add ${Math.ceil(shortfall)}
            </Button>
          }
        >
          <AlertTitle>Insufficient Funds</AlertTitle>
          <Typography variant="body2">
            You need ${shortfall.toFixed(2)} more to send this ${offerAmount.toFixed(2)} offer.
            {hasBlitzPayAccount && spendingAvailable === 0 && ' Your BlitzPay spending power is also at $0.'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Available:</strong> ${maxAffordableBudget.toFixed(2)} | <strong>Offer:</strong> ${offerAmount.toFixed(2)}
          </Typography>
        </Alert>
      );
    }

    if (offerAmount > 0) {
      return (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: alpha(theme.palette.success.main, 0.05),
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar
              sx={{
                bgcolor: alpha(theme.palette.success.main, 0.15),
                color: 'success.main',
              }}
            >
              <CheckCircleIcon />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={600} color="success.main">
                ✓ Sufficient Funds Available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You have ${maxAffordableBudget.toFixed(2)} available
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                <Chip 
                  label={`Balance: $${currentBalance.toFixed(2)}`}
                  size="small"
                  variant="outlined"
                  color="success"
                />
                {hasBlitzPayAccount && spendingAvailable > 0 && (
                  <Chip
                    label={`Spending Power: $${spendingAvailable.toFixed(2)}`}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: alpha(wizardPalette.accent, 0.26),
                      color: wizardPalette.accent,
                    }}
                  />
                )}
              </Stack>
            </Box>
            <Stack alignItems="flex-end" spacing={0.5}>
              <Typography variant="caption" color="text.secondary">
                After Offer
              </Typography>
              <Typography variant="h6" fontWeight={700} sx={{ color: wizardPalette.accent }}>
                ${(maxAffordableBudget - offerAmount).toFixed(2)}
              </Typography>
            </Stack>
          </Stack>
        </Paper>
      );
    }

    return null;
  };

  const renderStepContent = (stepIndex) => {
    switch (stepIndex) {
      case 0: // Partnership Details
      return (
          <Fade in timeout={300}>
          <Stack spacing={useCleanDialogStyle ? 2.4 : 3}>
              {/* Creator Info */}
              {!useCleanDialogStyle && (
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: { xs: 1.6, md: 2.4 }, 
                    border: `1px solid ${alpha(wizardPalette.ink, 0.14)}`,
                    borderRadius: 4,
                    backgroundColor: '#eef3f9',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.72), 0 1px 2px rgba(15, 23, 42, 0.03)'
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                      src={creator.pfphref || defaultPfp}
                      alt={creator.display_name}
                      sx={{
                        width: 56,
                        height: 56,
                        boxShadow: '0 6px 18px rgba(15, 23, 42, 0.12)'
                      }}
                    />
                    <Box flex={1}>
                      <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.25, color: wizardPalette.ink }}>
                        {creator.display_name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: wizardPalette.muted }}>
                        {isExternal ? 'Creating new partnership' : 'Existing partnership'}
                      </Typography>
                    </Box>
                    {onCancel && !isInDialog && (
                      <IconButton onClick={onCancel} size="small">
                        <CloseIcon />
                      </IconButton>
                    )}
                  </Stack>
                </Paper>
              )}

              {/* Core Form Fields */}
              <Paper elevation={0} sx={{ ...sectionCardSx, p: { xs: 2, md: useCleanDialogStyle ? 2.25 : 2.5 } }}>
                <Stack spacing={useCleanDialogStyle ? 2.05 : 2.25}>
                  {useCleanDialogStyle && (
                    <Box sx={{ maxWidth: 500 }}>
                      <Typography
                        sx={{
                          fontSize: { xs: '1.75rem', md: '1.95rem' },
                          lineHeight: 1.05,
                          fontWeight: 900,
                          letterSpacing: '-0.045em',
                          color: wizardPalette.ink,
                          mb: 1.2,
                        }}
                      >
                        Let's start with the basics.
                      </Typography>
                      <Typography
                        sx={{
                          color: wizardPalette.accentAlt,
                          fontSize: '0.86rem',
                          lineHeight: 1.55,
                          maxWidth: 480,
                        }}
                      >
                        {isExternal 
                          ? 'Define the partnership terms before sending the offer.'
                          : 'Define the offer terms before sending it to the creator.'
                        }
                      </Typography>
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: useCleanDialogStyle
                          ? 'minmax(0, 1.08fr) minmax(0, 0.72fr)'
                          : 'minmax(0, 1fr) minmax(0, 1fr)',
                      },
                      gap: { xs: 1.8, md: useCleanDialogStyle ? 1.8 : 2 },
                      width: '100%',
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={fieldGroupSx}>
                        <Typography sx={fieldLabelSx}>
                          {isExternal ? "Partnership Name" : "Offer Title"}
                        </Typography>
                        <TextField
                          fullWidth
                          value={formData.partnershipName}
                          onChange={handleChange('partnershipName')}
                          error={!!errors.partnershipName}
                          placeholder={isExternal ? "Name this partnership" : "Name this offer"}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <BusinessIcon sx={{ color: wizardPalette.accent, fontSize: 20 }} />
                              </InputAdornment>
                            )
                          }}
                          sx={wizardFieldSx}
                        />
                        {renderHelperText("Give this collaboration a memorable name", errors.partnershipName)}
                      </Box>
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={fieldGroupSx}>
                        <Typography sx={fieldLabelSx}>Offer Amount</Typography>
                        <TextField
                          fullWidth
                          type="number"
                          value={formData.offerAmount}
                          onChange={handleChange('offerAmount')}
                          error={!!errors.offerAmount}
                          placeholder="0.00"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AttachMoneyIcon sx={{ color: wizardPalette.accentAlt, fontSize: 20 }} />
                              </InputAdornment>
                            )
                          }}
                          sx={wizardFieldSx}
                        />
                        {renderHelperText("Total amount in USD", errors.offerAmount)}
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={descriptionPanelSx}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      justifyContent="space-between"
                      spacing={0.75}
                      sx={{ mb: 1 }}
                    >
                      <Typography sx={{ ...fieldLabelSx, mb: 0 }}>Description & Requirements</Typography>
                    </Stack>
                    <TextField
                      fullWidth
                      multiline
                      rows={useCleanDialogStyle ? 3 : 5}
                      value={formData.description}
                      onChange={handleChange('description')}
                      error={!!errors.description}
                      placeholder="Describe the collaboration scope, deliverables, expectations, and any specific requirements..."
                      sx={{
                        ...wizardFieldSx,
                        '& .MuiOutlinedInput-root': {
                          ...wizardFieldSx['& .MuiOutlinedInput-root'],
                          alignItems: 'flex-start',
                          py: useCleanDialogStyle ? 0.65 : 0.85,
                          bgcolor: alpha('#ffffff', 0.94),
                        },
                      }}
                    />
                    {renderHelperText("Describe what this collaboration includes", errors.description)}
                  </Box>
                </Stack>
              </Paper>

              {/* Advanced Options Accordion */}
              <Paper 
                elevation={0}
                sx={{ 
                  ...sectionCardSx,
                  overflow: 'hidden',
                  boxShadow: useCleanDialogStyle ? sectionCardSx.boxShadow : sectionCardSx.boxShadow,
                }}
              >
                <Box
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  sx={{
                    p: { xs: 1.7, md: useCleanDialogStyle ? 1.8 : 2.2 },
                    cursor: 'pointer',
                    backgroundColor: useCleanDialogStyle ? '#ffffff' : wizardPalette.surfaceMuted,
                    borderBottom: showAdvanced ? `1px solid ${wizardPalette.border}` : 'none',
                    '&:hover': {
                      backgroundColor: alpha(wizardPalette.accent, 0.06),
                    }
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar
                      sx={{
                        width: useCleanDialogStyle ? 30 : 32,
                        height: useCleanDialogStyle ? 30 : 32,
                        bgcolor: alpha(wizardPalette.accent, 0.1),
                        color: wizardPalette.accent,
                      }}
                    >
                      <SettingsIcon fontSize="small" />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: wizardPalette.ink }}>
                        Advanced Options
                      </Typography>
                      {!useCleanDialogStyle && (
                        <Typography variant="caption" sx={{ color: wizardPalette.muted }}>
                          Refine the deliverable terms without changing the main offer flow.
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      size="small"
                      label={showAdvanced ? 'Hide options' : 'Optional'}
                      sx={{
                        bgcolor: alpha(wizardPalette.accentAlt, 0.1),
                        color: wizardPalette.accentAlt,
                        fontWeight: 700,
                        borderRadius: 999,
                        border: `1px solid ${alpha(wizardPalette.accentAlt, 0.14)}`,
                      }}
                    />
                    <ExpandMoreIcon 
                      sx={{ 
                        color: wizardPalette.muted,
                        transform: showAdvanced ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease'
                      }} 
                    />
                  </Stack>
                </Box>

                <Collapse in={showAdvanced}>
                  <Box sx={{ p: { xs: 1.9, md: useCleanDialogStyle ? 2.25 : 2.5 }, bgcolor: wizardPalette.surface }}>
                    <Grid container spacing={useCleanDialogStyle ? 2.1 : 2}>
                      <Grid item xs={12} md={6}>
                        <Box sx={fieldGroupSx}>
                          <Typography sx={fieldLabelSx}>Deliverable Type</Typography>
                          <FormControl fullWidth>
                            <Select
                              value={formData.deliverableType}
                              onChange={handleChange('deliverableType')}
                              sx={{
                                ...wizardFieldSx,
                                '& .MuiOutlinedInput-root': wizardFieldSx['& .MuiOutlinedInput-root'],
                                borderRadius: useCleanDialogStyle ? 1.4 : 3,
                                bgcolor: wizardPalette.surface,
                              }}
                              renderValue={(selected) => {
                                const option = deliverableTypeOptions.find((item) => item.value === selected);
                                return (
                                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.4 }}>
                                    <Box
                                      sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: 1.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: alpha(wizardPalette.accent, 0.08),
                                      }}
                                    >
                                      <Box sx={{ fontSize: '0.78rem', lineHeight: 1 }}>{option?.icon}</Box>
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.2, color: wizardPalette.ink }}>
                                        {option?.label}
                                      </Typography>
                                      <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'normal', color: wizardPalette.muted }}>
                                        {option?.description}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                );
                              }}
                            >
                              {deliverableTypeOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.5 }}>
                                    <Box sx={{ fontSize: '0.9rem', lineHeight: 1 }}>{option.icon}</Box>
                                    <Box>
                                      <Typography variant="body1">{option.label}</Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {option.description}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box sx={fieldGroupSx}>
                          <Typography sx={fieldLabelSx}>Due Date</Typography>
                          <TextField
                            fullWidth
                            type="date"
                            value={formData.dueDate}
                            onChange={handleChange('dueDate')}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <CalendarTodayIcon sx={{ color: wizardPalette.accentWarm, fontSize: 20 }} />
                                </InputAdornment>
                              )
                            }}
                            sx={wizardFieldSx}
                          />
                          {renderHelperText("Defaults to 7 days from now")}
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        <Box sx={fieldGroupSx}>
                          <Typography sx={fieldLabelSx}>Platforms</Typography>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, minmax(0, 1fr))',
                                md: 'repeat(3, minmax(0, 1fr))',
                              },
                              gap: 1,
                            }}
                          >
                            {platformOptions.map((platform) => {
                              const IconComponent = platform.icon;
                              const isSelected = formData.platforms.includes(platform.value);

                              return (
                                <Box
                                  component="label"
                                  key={platform.value}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.85,
                                    minHeight: 42,
                                    px: 1.15,
                                    py: 0.65,
                                    borderRadius: 1.4,
                                    cursor: 'pointer',
                                    border: `1px solid ${isSelected ? wizardPalette.accent : '#cbd5e1'}`,
                                    bgcolor: isSelected ? alpha(wizardPalette.accent, 0.07) : wizardPalette.surface,
                                    transition: 'border-color 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease',
                                    '&:hover': {
                                      borderColor: isSelected ? wizardPalette.accent : '#9aa6b2',
                                      bgcolor: isSelected ? alpha(wizardPalette.accent, 0.09) : '#fbfaf8',
                                    },
                                  }}
                                >
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={() => handlePlatformToggle(platform.value)}
                                    size="small"
                                    sx={{
                                      p: 0,
                                      color: '#94a3b8',
                                      '&.Mui-checked': {
                                        color: wizardPalette.accent,
                                      },
                                    }}
                                  />
                                  <IconComponent fontSize="small" color={platform.color} />
                                  <Typography sx={{ fontWeight: 700, color: wizardPalette.ink, fontSize: '0.86rem' }}>
                                    {platform.value}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box sx={fieldGroupSx}>
                          <Typography sx={fieldLabelSx}>Revisions Included</Typography>
                          <TextField
                            fullWidth
                            type="number"
                            value={formData.revisions}
                            onChange={handleChange('revisions')}
                            placeholder="e.g., 2"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <EditIcon sx={{ color: wizardPalette.accentWarm, fontSize: 20 }} />
                                </InputAdornment>
                              )
                            }}
                            sx={wizardFieldSx}
                          />
                          {renderHelperText("How many revisions are included?")}
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box sx={fieldGroupSx}>
                          <Typography sx={fieldLabelSx}>Script Requirements</Typography>
                          <TextField
                            fullWidth
                            value={formData.scriptRequirements}
                            onChange={handleChange('scriptRequirements')}
                            placeholder="Any special script requirements?"
                            sx={wizardFieldSx}
                          />
                          {renderHelperText("Include any script or talking-point requirements")}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Collapse>
              </Paper>
            </Stack>
          </Fade>
        );

      case 1: // Review & Send
        return (
          <Fade in timeout={300}>
            <Stack spacing={3}>
              {/* Affordability Check - Only for internal offers */}
              {renderAffordabilityCheck()}

              {isExternal && !currentToken && (
                <TextField
                  fullWidth
                  type="email"
                  label="Email Address"
                  value={formData.email}
                  onChange={handleChange('email')}
                  error={!!errors.email}
                  helperText={errors.email || "We'll use this to contact you about the partnership"}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              )}

              {/* Summary Card */}
              <Card 
                variant="outlined" 
                sx={{ 
                  borderRadius: 5,
                  border: `1px solid ${alpha(wizardPalette.ink, 0.09)}`,
                  backgroundColor: wizardPalette.surface,
                  boxShadow: '0 12px 28px rgba(15, 23, 42, 0.06)'
                }}
              >
                <CardHeader
                  title={isExternal ? "Partnership Summary" : "Offer Summary"}
                  titleTypographyProps={{ variant: 'h6', sx: { color: wizardPalette.ink, fontWeight: 800 } }}
                  sx={{ pb: 1, '& .MuiCardHeader-content': { minWidth: 0 } }}
                />
                <CardContent sx={{ pt: 0 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Partnership Name
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        {formData.partnershipName}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Amount
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: wizardPalette.accentAlt }}>
                        ${parseFloat(formData.offerAmount || 0).toFixed(2)}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Due Date
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {new Date(formData.dueDate).toLocaleDateString()}
                      </Typography>
                    </Grid>

                    {formData.platforms.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Platforms
                        </Typography>
                        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                          {formData.platforms.map((platformValue) => {
                            const platform = platformOptions.find(p => p.value === platformValue);
                            const IconComponent = platform?.icon;
                            return (
                              <Chip
                                key={platformValue}
                                icon={IconComponent ? <IconComponent fontSize="small" /> : undefined}
                                label={platformValue}
                                size="small"
                                variant="filled"
                                sx={{ borderRadius: 999, bgcolor: alpha(wizardPalette.accent, 0.08) }}
                              />
                            );
                          })}
                        </Stack>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Description
                      </Typography>
                      <Box
                        sx={{
                          mt: 0.5,
                          p: 2,
                          borderRadius: 3,
                          bgcolor: wizardPalette.surfaceAccent,
                          border: `1px solid ${alpha(wizardPalette.accentAlt, 0.14)}`,
                        }}
                      >
                        <Typography variant="body1">
                          {formData.description}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {!isExternal && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.sendNotifications}
                      onChange={handleChange('sendNotifications')}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: wizardPalette.accent,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          bgcolor: alpha(wizardPalette.accent, 0.46),
                        },
                      }}
                    />
                  }
                  label="Send notifications to creator"
                />
              )}
            </Stack>
          </Fade>
        );

      default:
        return null;
    }
  };

  const isLastStep = activeStep === steps.length - 1;

  // Adjust container props based on dialog usage
  const containerProps = isInDialog 
    ? {
        maxWidth: false,
        disableGutters: true,
        sx: { 
          width: '100%',
          maxWidth: useCleanDialogStyle ? { xs: '100%', md: 780 } : '100%',
          mx: useCleanDialogStyle ? 'auto' : undefined,
          px: { xs: 1.4, md: useCleanDialogStyle ? 0 : 2 },
          py: { xs: 1.35, md: useCleanDialogStyle ? 1.85 : 1.75 }
        }
      }
    : {
        maxWidth: "md",
        sx: { py: { xs: 2, md: 3 } }
      };

  return (
    <>
      <AlertDialog alertState={dialogState} />
      <AddBalanceDialog
        open={showAddBalanceDialog}
        onClose={() => setShowAddBalanceDialog(false)}
        onSuccess={handleAddBalanceSuccess}
      />
      
      <Container {...containerProps}>
        <Box sx={{ width: '100%' }}>
          {/* Header - Hide in dialog since dialog has its own header */}
          {!isInDialog && (
            <Box textAlign="center" mb={4}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {isExternal ? 'Create Partnership' : 'Send Offer'}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {isExternal 
                  ? 'Set up a new collaboration partnership'
                  : 'Add a new offer to your existing partnership'
                }
              </Typography>
            </Box>
          )}

          {/* Stepper */}
          <Paper
            elevation={0}
            sx={{
              mb: useCleanDialogStyle ? 3.2 : 1.1,
              p: { xs: useCleanDialogStyle ? 0 : 1.05, md: useCleanDialogStyle ? 0 : 0.95 },
              borderRadius: useCleanDialogStyle ? 0 : 4,
              border: useCleanDialogStyle ? 'none' : `1px solid ${alpha(wizardPalette.ink, 0.18)}`,
              bgcolor: useCleanDialogStyle ? 'transparent' : wizardPalette.surface,
              backgroundImage: useCleanDialogStyle
                ? 'none'
                : `linear-gradient(180deg, ${alpha(wizardPalette.accent, 0.03)} 0%, ${wizardPalette.surface} 100%)`,
              boxShadow: useCleanDialogStyle ? 'none' : '0 8px 18px rgba(15, 23, 42, 0.035)',
            }}
          >
            {useCleanDialogStyle ? (
              <Box>
                <Grid container columns={2} sx={{ mb: 1.4 }}>
                  {steps.map((step, index) => {
                    const isActive = activeStep === index;
                    return (
                      <Grid item xs={1} key={step.label}>
                        <Typography
                          sx={{
                            color: isActive ? wizardPalette.accent : '#aa9d9a',
                            fontWeight: 800,
                            fontSize: '0.68rem',
                            mb: 0.55,
                          }}
                        >
                          Step {index + 1}
                        </Typography>
                        <Typography
                          sx={{
                            color: isActive ? wizardPalette.ink : '#a59a97',
                            fontWeight: isActive ? 900 : 600,
                            fontSize: { xs: '0.88rem', md: '0.95rem' },
                          }}
                        >
                          {step.label}
                        </Typography>
                      </Grid>
                    );
                  })}
                </Grid>
                <Box sx={{ height: 5, borderRadius: 999, bgcolor: '#ebe7e4', overflow: 'hidden' }}>
                  <Box
                    sx={{
                      width: `${((activeStep + 1) / steps.length) * 100}%`,
                      height: '100%',
                      borderRadius: 999,
                      bgcolor: wizardPalette.accent,
                      transition: 'width 0.25s ease',
                    }}
                  />
                </Box>
              </Box>
            ) : (
              <Stack direction="column" alignItems="stretch" spacing={0}>
                {isInDialog && !useCleanDialogStyle && (
                  <Box sx={{ mb: 0.35 }}>
                    <Typography variant="overline" sx={{ color: wizardPalette.accentAlt, letterSpacing: '0.14em', fontWeight: 800, fontSize: '0.68rem' }}>
                      Offer Flow
                    </Typography>
                    <Typography variant="body2" sx={{ color: wizardPalette.muted, maxWidth: 500, fontSize: '0.82rem', lineHeight: 1.35 }}>
                      Build the offer, review it, and send it into this partnership chat.
                    </Typography>
                  </Box>
                )}

                <Stepper
                  activeStep={activeStep}
                  alternativeLabel={!isMobile}
                  sx={{
                    flex: 1,
                    '& .MuiStepConnector-line': {
                      borderTopWidth: 2,
                      borderColor: alpha(wizardPalette.accent, 0.28),
                    },
                    '& .MuiStep-root': {
                      padding: isMobile ? '0 1px' : '0 6px',
                    },
                    '& .MuiStepLabel-labelContainer': {
                      mt: 0.05,
                    },
                  }}
                >
                  {steps.map((step, index) => (
                    <Step key={step.label} completed={completedSteps.has(index)}>
                      <StepLabel
                        StepIconComponent={({ active, completed }) => {
                          const IconComponent = step.icon;
                          return (
                            <Box
                              sx={{
                                width: 26,
                                height: 26,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: completed 
                                  ? '#94a3b8' 
                                  : active 
                                    ? wizardPalette.accent
                                    : '#e5e7eb',
                                color: 'white',
                                transition: 'all 0.3s ease',
                                boxShadow: active ? `0 0 0 2px ${alpha(wizardPalette.accent, 0.14)}` : 'none'
                              }}
                            >
                              {completed ? (
                                <CheckCircleIcon sx={{ fontSize: 14 }} />
                              ) : (
                                <IconComponent sx={{ fontSize: 14 }} />
                              )}
                            </Box>
                          );
                        }}
                      >
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: 700,
                            fontSize: '0.72rem',
                            color: wizardPalette.ink,
                          }}
                        >
                          {isMobile ? step.mobileLabel : step.label}
                        </Typography>
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Stack>
            )}
          </Paper>

          {/* Step Content */}
          <Paper 
            elevation={0}
            sx={{ 
              p: { xs: 1.4, md: useCleanDialogStyle ? 0.6 : 2.5 }, 
              mb: useCleanDialogStyle ? 2.5 : 2,
              minHeight: useCleanDialogStyle ? 300 : 400,
              border: useCleanDialogStyle ? 'none' : `1px solid ${alpha(wizardPalette.ink, 0.13)}`,
              borderRadius: useCleanDialogStyle ? 4 : 5,
              backgroundColor: useCleanDialogStyle ? 'transparent' : wizardPalette.surface,
              boxShadow: useCleanDialogStyle ? 'none' : '0 12px 32px rgba(15, 23, 42, 0.05)'
            }}
          >
            {renderStepContent(activeStep)}
          </Paper>

          {/* Actions */}
          <Stack 
            direction="row" 
            justifyContent="space-between" 
            spacing={2}
            sx={{ 
              pt: useCleanDialogStyle ? 0 : 0.5,
              pb: isMobile && isInDialog ? 2 : 0,
            }}
          >
            <Button
              onClick={activeStep === 0 ? onCancel : handleBack}
              variant="outlined"
              startIcon={activeStep === 0 ? null : <NavigateBeforeIcon />}
              disabled={createPartnershipMutation.isLoading}
              sx={{ 
                minWidth: 100,
                color: useCleanDialogStyle ? '#475569' : wizardPalette.ink,
                borderColor: useCleanDialogStyle ? '#d6dee9' : alpha(wizardPalette.ink, 0.12),
                bgcolor: useCleanDialogStyle ? '#ffffff' : alpha('#ffffff', 0.75),
                borderRadius: 999,
                py: useCleanDialogStyle ? 1.05 : 1.2,
                px: useCleanDialogStyle ? 2.8 : 3,
                fontWeight: 700,
                '&:hover': {
                  borderColor: useCleanDialogStyle ? '#b9c4d3' : alpha(wizardPalette.accent, 0.24),
                  bgcolor: useCleanDialogStyle ? '#f8fafc' : alpha(wizardPalette.accent, 0.04),
                }
              }}
            >
              {activeStep === 0 ? 'Cancel' : 'Back'}
            </Button>

            <Button
              onClick={isLastStep ? handleSubmit : handleNext}
              variant="contained"
              endIcon={
                createPartnershipMutation.isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : isLastStep ? (
                  isExternal ? <CreateIcon /> : <SendIcon />
                ) : (
                  <NavigateNextIcon />
                )
              }
              disabled={createPartnershipMutation.isLoading || (isLastStep && shouldCheckBudget && (!canAffordOffer || isLoadingFunds))}
              sx={{ 
                minWidth: 140,
                borderRadius: 999,
                py: useCleanDialogStyle ? 1.08 : 1.2,
                px: useCleanDialogStyle ? 3.2 : 3,
                fontWeight: 800,
                bgcolor: wizardPalette.accent,
                boxShadow: useCleanDialogStyle ? '0 12px 26px rgba(49, 87, 213, 0.24)' : '0 10px 24px rgba(37, 99, 235, 0.24)',
                '&:hover': {
                  bgcolor: wizardPalette.accent,
                  filter: 'brightness(0.98)',
                  boxShadow: useCleanDialogStyle ? '0 14px 30px rgba(49, 87, 213, 0.28)' : '0 12px 28px rgba(37, 99, 235, 0.28)',
                }
              }}
            >
              {isLastStep ? (
                isExternal ? 'Create Partnership' : 'Send Offer'
              ) : (
                'Next Step'
              )}
            </Button>
          </Stack>
        </Box>
      </Container>
    </>
  );
}
